import { useMemo, lazy, Suspense } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CHART } from "./theme/brand";

const MapView = lazy(() => import("./MapClientesMap"));

const ZONE_COLORS = CHART.series;

export default function MapClientes({ store }) {
  const zonas = store.config.zonas || [];
  const hasZonas = zonas.length > 0;
  const leadsConZona = store.leads.filter(l => l.zona);
  const leadsConUbicacion = useMemo(() => store.leads.filter(l => l.location?.lat), [store.leads]);

  const zonaColorMap = useMemo(() => {
    const map = {};
    zonas.forEach((z, i) => { map[z] = ZONE_COLORS[i % ZONE_COLORS.length]; });
    return map;
  }, [zonas]);

  const stats = useMemo(() => {
    if (!hasZonas) return [];
    const maxLeads = Math.max(...zonas.map(z => store.leads.filter(l => l.zona === z).length), 1);
    return zonas.map((zona, i) => {
      const leads = store.leads.filter(l => l.zona === zona);
      const inscritos = leads.filter(l => l.etapa === "inscrito").length;
      const sinContacto = leads.filter(l => {
        if (l.etapa === "inscrito" || l.etapa === "perdido") return false;
        if (!l.ultimoContacto) return true;
        return (Date.now() - new Date(l.ultimoContacto).getTime()) >= 3 * 86400000;
      }).length;
      const ventas = store.ventasActivas.filter(v => {
        const lead = store.leads.find(l => l.id === v.leadId);
        return lead?.zona === zona;
      });
      const totalVentas = ventas.reduce((s, v) => s + v.total, 0);
      return {
        zona, leads: leads.length, inscritos, sinContacto, totalVentas,
        pct: Math.round((leads.length / maxLeads) * 100),
        color: ZONE_COLORS[i % ZONE_COLORS.length],
      };
    });
  }, [zonas, store.leads, store.ventasActivas, hasZonas]);

  if (!hasZonas && leadsConZona.length === 0 && leadsConUbicacion.length === 0) return null;

  return (
    <Card variant="outlined" sx={{ mt: 3 }} data-onboarding-id="onb-mapa-clientes">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={2}>
          <PlaceIcon color="primary" />
          <Typography variant="subtitle1" sx={{ mr: "auto" }}>Mapa de clientes</Typography>
          {leadsConUbicacion.length > 0 && (
            <Chip label={`${leadsConUbicacion.length} en mapa`} size="small" color="primary" variant="outlined" />
          )}
          {leadsConZona.length > 0 && (
            <Chip label={`${leadsConZona.length} con zona`} size="small" variant="outlined" />
          )}
        </Stack>

        {/* Mapa real con marcadores */}
        {leadsConUbicacion.length > 0 && (
          <Box sx={{ mb: 2, borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider" }}>
            <Suspense fallback={<Box sx={{ height: 320, bgcolor: "grey.100", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography variant="caption" color="text.secondary">Cargando mapa…</Typography></Box>}>
              <MapView leads={leadsConUbicacion} zonaColorMap={zonaColorMap} moneda={store.config.moneda} defaultCenter={store.config.ubicacionNegocio} config={store.config} />
            </Suspense>
          </Box>
        )}

        <Grid container spacing={2}>
          {stats.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.zona}>
              <Card variant="outlined" sx={{ borderLeft: `4px solid ${s.color}` }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, bgcolor: s.color, fontSize: 13 }}>
                        {s.zona.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={700}>{s.zona}</Typography>
                    </Stack>
                    <Chip label={`${s.leads} leads`} size="small" sx={{ fontWeight: 700 }} />
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={s.pct}
                    sx={{ mb: 1, height: 6, borderRadius: 3, bgcolor: "divider",
                      "& .MuiLinearProgress-bar": { bgcolor: s.color, borderRadius: 3 } }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    <Chip label={`${s.inscritos} convertidos`} size="small" color="success" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    {s.totalVentas > 0 && (
                      <Chip label={`${store.config.moneda} ${s.totalVentas.toLocaleString()}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    )}
                    {s.sinContacto > 0 && (
                      <Chip icon={<WarningAmberIcon sx={{ fontSize: "14px !important" }} />}
                        label={`${s.sinContacto} sin contacto`} size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Zonas sin cobertura */}
          {store.zonasSinCobertura.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, bgcolor: "tint.warning", borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700} color="warning.main">
                  📍 Zonas sin cobertura: {store.zonasSinCobertura.join(", ")}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
